"use client";

/**
 * IndexerConsoleMock — STAGED mock of the on-device operator console that an
 * Indexer OS node shows on its own screen. Lives on /indexer_os.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THIS IS A MOCK AND NOT A SCREENSHOT — do not replace it with a capture.
 *
 * The real console shows, all on one screen: the VPN egress IP and exit
 * controls, the node's LAN address and MAC, the tunnel address, a live server
 * log naming internal API routes and the local model port, per-domain crawl
 * detail naming real sites, and a connected root shell. Published together
 * that is a working map of the architecture plus the fleet's egress identity —
 * the same exposure as incident V-103.
 *
 * So this is art-directed instead: the real layout and rhythm (fixed header,
 * card grid, terminal), with every revealing element replaced by design —
 * no addresses, no logs, no real domains, and a terminal sitting at a neutral
 * idle prompt rather than a live shell. It is also better marketing, because
 * the frame shows a healthy node rather than whatever the box happened to be
 * doing.
 *
 * Anything added here must stay at the OUTCOME level: what the operator can
 * see and act on, never how the system achieves it.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { useEffect, useRef, useState } from "react";
import {
  Activity,
  Check,
  Cpu,
  Download,
  Gauge,
  HardDrive,
  Library,
  Landmark,
  Newspaper,
  RefreshCw,
  ShieldCheck,
  Terminal,
  Wifi,
} from "lucide-react";

/**
 * Reading targets are described by CATEGORY, never by domain. Naming real
 * sites would publish what the fleet crawls; inventing domains would be a
 * fabricated claim. A category is true and reveals nothing.
 */
const READING = [
  { icon: Library, label: "Public research archive", pages: "1,204 pages", state: "reading" },
  { icon: Landmark, label: "Government publications", pages: "860 pages", state: "reading" },
  { icon: Newspaper, label: "Regional news archive", pages: "2,317 pages", state: "reading" },
  { icon: Library, label: "Standards body library", pages: "446 pages", state: "queued" },
  { icon: Landmark, label: "Municipal records", pages: "1,038 pages", state: "queued" },
  { icon: Newspaper, label: "Open journal index", pages: "5,902 pages", state: "reading" },
];

const HEALTH = [
  { label: "Reading", ok: true },
  { label: "Connection protected", ok: true },
  { label: "Graphics card", ok: true },
  { label: "Updates", ok: true },
];

export function IndexerConsoleMock() {
  const [offset, setOffset] = useState(0);
  const [tick, setTick] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const armed = useRef(false);
  const timers = useRef<number[]>([]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const clearTimers = () => {
      timers.current.forEach((id) => clearTimeout(id));
      timers.current = [];
    };
    const schedule = (fn: () => void, delay: number) => {
      const id = window.setTimeout(fn, delay);
      timers.current.push(id);
    };

    const step = () => {
      setOffset((o) => (o + 1) % READING.length);
      setTick((t) => t + 1);
      schedule(step, 4000);
    };

    const obs = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || armed.current) return;
        armed.current = true;
        schedule(step, 1800);
        obs.disconnect();
      },
      { threshold: 0.25 }
    );
    obs.observe(el);
    return () => {
      obs.disconnect();
      clearTimers();
    };
  }, []);

  // Deterministic wiggle so the server render and first client paint agree.
  const cpu = 34 + ((tick * 7) % 24);
  const ram = 41 + ((tick * 5) % 18);
  const gpu = 58 + ((tick * 11) % 30);
  const pages = 48213 + tick * 37;

  const visible = Array.from({ length: 3 }, (_, i) => READING[(offset + i) % READING.length]);

  return (
    <div ref={ref} className="relative w-full max-w-5xl mx-auto">
      <div aria-hidden className="absolute -inset-x-4 -inset-y-4 rounded-[28px] bg-foreground/[0.02] blur-2xl" />

      <div className="relative rounded-[14px] border hairline-strong bg-card/85 backdrop-blur-md overflow-hidden shadow-[0_24px_48px_-24px_hsl(var(--foreground)/0.18)]">
        {/* Fixed header — node identity and what it's doing. No addresses. */}
        <div className="relative flex items-center px-3 h-10 border-b hairline bg-foreground/[0.04] text-[10.5px] font-mono tracking-[0.04em] text-foreground/65">
          <div className="inline-flex items-center gap-1.5">
            <Activity className="h-3 w-3 text-[hsl(var(--accent))]" />
            <span>askfinz-indexer-014</span>
          </div>
          <div className="absolute left-1/2 -translate-x-1/2 hidden md:inline-flex items-center gap-2">
            <span className="inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
            <span>indexing · 3 in progress</span>
          </div>
          <div className="ml-auto inline-flex items-center gap-2.5">
            <span>uptime 9d 04:17</span>
          </div>
        </div>

        <div className="grid grid-cols-12 min-h-[480px] md:min-h-[540px]">
          {/* Left rail — this machine */}
          <aside className="col-span-12 md:col-span-3 border-r hairline bg-foreground/[0.03] p-3">
            <div className="text-[10px] tracking-[0.16em] uppercase text-foreground/65 font-mono mb-2">
              This machine
            </div>
            <div className="space-y-1.5">
              <Meter icon={Cpu} label="Processor" value={`${cpu}%`} sub="8 cores · shared" pct={cpu} />
              <Meter icon={HardDrive} label="Memory" value={`${ram}%`} sub="capped by design" pct={ram} />
              <Meter icon={Gauge} label="Graphics card" value={`${gpu}%`} sub="serving the pool" pct={gpu} tone="accent" />
            </div>

            <div className="mt-4 text-[10px] tracking-[0.16em] uppercase text-foreground/65 font-mono mb-2">
              Health
            </div>
            <ul className="text-[10.5px] space-y-1">
              {HEALTH.map((h) => (
                <li key={h.label} className="flex items-center gap-1.5">
                  <Check className="h-3 w-3 text-emerald-500" strokeWidth={2.5} />
                  <span className="text-foreground/75">{h.label}</span>
                </li>
              ))}
            </ul>

            <div className="mt-4 rounded-md border hairline bg-background/65 px-2.5 py-2">
              <div className="flex items-center gap-1.5">
                <ShieldCheck className="h-3 w-3 text-foreground/65" />
                <span className="text-[11px] font-medium text-foreground/85">Connection</span>
              </div>
              <div className="mt-1 text-[10px] font-mono text-foreground/65">
                Protected · rotates on its own
              </div>
            </div>
          </aside>

          {/* Centre — what it's reading */}
          <main className="col-span-12 md:col-span-6 relative flex flex-col bg-background overflow-hidden">
            <div className="px-3 py-2 border-b hairline flex items-center gap-2 bg-foreground/[0.02]">
              <span className="text-[10px] font-mono tracking-[0.14em] uppercase text-foreground/65">
                Reading now
              </span>
              <span className="ml-auto inline-flex items-center gap-1.5 text-[10px] font-mono text-foreground/65">
                <RefreshCw className="h-3 w-3" />
                live
              </span>
            </div>

            <div className="relative flex-1 p-3">
              <div aria-hidden className="absolute inset-0 grid-bg-dense opacity-[0.10]" />

              <div className="relative space-y-1.5">
                {visible.map((r, i) => {
                  const Icon = r.icon;
                  const isReading = r.state === "reading";
                  return (
                    <div
                      key={`${r.label}-${tick}-${i}`}
                      className="rounded-md border hairline bg-background/80 p-2.5"
                      style={{ animation: "blur-fade-in 0.45s ease-out both", animationDelay: `${i * 70}ms` }}
                    >
                      <div className="flex items-center gap-2">
                        <span className="inline-flex h-6 w-6 items-center justify-center rounded-md border hairline">
                          <Icon className="h-3 w-3" />
                        </span>
                        <span className="text-[11.5px] font-medium text-foreground/85 truncate">
                          {r.label}
                        </span>
                        <span
                          className={
                            "ml-auto inline-flex items-center rounded-full border px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-[0.14em] " +
                            (isReading
                              ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30"
                              : "border-foreground/20 text-foreground/60")
                          }
                        >
                          {r.state}
                        </span>
                      </div>
                      <div className="mt-1 pl-8 text-[10.5px] font-mono text-foreground/65">
                        {r.pages}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="relative mt-3 grid grid-cols-3 gap-1.5">
                <Tile label="Filed today" value={pages.toLocaleString()} />
                <Tile label="Re-checked" value="2,904" />
                <Tile label="Set aside" value="311" />
              </div>

              {/* Terminal — deliberately idle. Never a live shell. */}
              <div className="relative mt-3 rounded-md border hairline bg-foreground/[0.04] overflow-hidden">
                <div className="flex items-center gap-1.5 px-2.5 py-1.5 border-b hairline">
                  <Terminal className="h-3 w-3 text-foreground/65" />
                  <span className="text-[10px] font-mono tracking-[0.14em] uppercase text-foreground/65">
                    Terminal
                  </span>
                  <span className="ml-auto text-[9.5px] font-mono text-foreground/45">closed</span>
                </div>
                <div className="px-2.5 py-2 font-mono text-[10.5px] text-foreground/45">
                  <span className="text-foreground/30">$</span> <span className="cursor" />
                </div>
              </div>
            </div>
          </main>

          {/* Right rail — operator controls */}
          <aside className="col-span-12 md:col-span-3 border-l hairline bg-foreground/[0.02] p-3 flex flex-col">
            <div className="text-[10px] tracking-[0.16em] uppercase text-foreground/65 font-mono mb-2">
              Operator
            </div>

            <div className="space-y-1.5">
              <Control icon={Wifi} label="Network" detail="Wired · connected" />
              <Control icon={ShieldCheck} label="Privacy" detail="On · automatic" />
              <Control icon={Download} label="Updates" detail="Up to date" />
              <Control icon={Activity} label="Connection test" detail="All checks passed" />
            </div>

            <div className="mt-4 text-[10px] tracking-[0.16em] uppercase text-foreground/65 font-mono mb-2">
              Since first boot
            </div>
            <dl className="space-y-1.5 text-[10.5px]">
              <div className="flex items-baseline justify-between">
                <dt className="text-foreground/65">Pages filed</dt>
                <dd className="font-mono text-foreground/85">194,530</dd>
              </div>
              <div className="flex items-baseline justify-between">
                <dt className="text-foreground/65">Days running</dt>
                <dd className="font-mono text-foreground/85">9</dd>
              </div>
              <div className="flex items-baseline justify-between">
                <dt className="text-foreground/65">Setup steps</dt>
                <dd className="font-mono text-foreground/85">0</dd>
              </div>
            </dl>

            <div className="mt-auto pt-3 text-[9.5px] font-mono tracking-[0.14em] uppercase text-foreground/45">
              on-device · no sign-in needed
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}

function Meter({
  icon: Icon,
  label,
  value,
  sub,
  pct,
  tone,
}: {
  icon: typeof Cpu;
  label: string;
  value: string;
  sub: string;
  pct: number;
  tone?: "accent";
}) {
  const clamped = Math.max(0, Math.min(100, pct));
  return (
    <div className="rounded-md border hairline bg-background/65 px-2.5 py-2">
      <div className="flex items-center gap-1.5">
        <Icon className="h-3 w-3 text-foreground/65" />
        <span className="text-[11px] font-medium text-foreground/85">{label}</span>
        <span className="ml-auto text-[10.5px] font-mono text-foreground/75">{value}</span>
      </div>
      <div className="mt-1 h-1 w-full rounded-full bg-foreground/10 overflow-hidden">
        <div
          className={
            "h-full transition-[width] duration-700 ease-out " +
            (tone === "accent" ? "bg-[hsl(var(--accent))]" : "bg-foreground/55")
          }
          style={{ width: `${clamped}%` }}
        />
      </div>
      <div className="mt-1 text-[9.5px] font-mono text-foreground/65">{sub}</div>
    </div>
  );
}

function Tile({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border hairline bg-background/80 px-2.5 py-2">
      <div className="text-[15px] font-semibold tabular-num leading-none">{value}</div>
      <div className="mt-1 text-[9.5px] font-mono tracking-[0.12em] uppercase text-foreground/55">
        {label}
      </div>
    </div>
  );
}

function Control({
  icon: Icon,
  label,
  detail,
}: {
  icon: typeof Cpu;
  label: string;
  detail: string;
}) {
  return (
    <div className="rounded-md border hairline bg-background/65 px-2.5 py-2">
      <div className="flex items-center gap-1.5">
        <Icon className="h-3 w-3 text-foreground/65" />
        <span className="text-[11px] font-medium text-foreground/85">{label}</span>
      </div>
      <div className="mt-0.5 pl-[18px] text-[10px] font-mono text-foreground/60">{detail}</div>
    </div>
  );
}
